import { toast } from "sonner";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Building2, Send, Gamepad2, IndianRupee, ShieldAlert,
  ArrowRight, CheckCircle2, MapPin,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const KPI = ({ icon: Icon, label, value, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-heading text-3xl font-semibold mt-1 tabular-nums">{value}</p>
      </div>
      <div className="h-10 w-10 rounded-xl bg-primary/20 text-primary grid place-items-center"><Icon className="h-5 w-5" /></div>
    </div>
  </Card>
);

export default function SuperAdminDashboardPage() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [integrity, setIntegrity] = useState(null);
  const [integrityLoading, setIntegrityLoading] = useState(true);

  useEffect(() => { api.superadminAnalytics().then((r) => setD(r.data)).catch(() => toast.error("Couldn't load analytics")).finally(() => setLoading(false)); }, []);

  // City-integrity widget — polls the same endpoint the full page uses.
  // We only need the totals, but it's the same call so no extra route
  // is needed. Failures degrade silently (widget just hides).
  useEffect(() => {
    let cancelled = false;
    api.superadminCityIntegrityList()
      .then((r) => { if (!cancelled) setIntegrity(r.data); })
      .catch(() => { if (!cancelled) setIntegrity({ error: true }); })
      .finally(() => { if (!cancelled) setIntegrityLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const breakdown = d?.message_breakdown || {};
  const pieData = Object.entries(breakdown).map(([k, v]) => ({ name: k, value: v }));
  const COLORS = ["#F5A800", "#22C55E", "#22D3EE", "#EF4444"];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Platform overview</h1>
        <p className="text-muted-foreground text-sm">Vendors, revenue, and growth signals.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loading || !d ? (
          [0, 1, 2, 3].map((i) => <Card key={i} className="p-5"><Skeleton className="h-16 w-full" /></Card>)
        ) : (
          <>
            <KPI icon={Building2} label="Vendors" value={d.vendors.total} testid="sa-kpi-vendors" />
            <KPI icon={Gamepad2} label="Campaigns" value={d.campaigns} testid="sa-kpi-campaigns" />
            <KPI icon={Send} label="Messages sent" value={d.total_messages} testid="sa-kpi-messages" />
            <KPI icon={IndianRupee} label="Revenue" value={`₹${Number(d.revenue_actual ?? 0).toLocaleString("en-IN")}`} testid="sa-kpi-revenue" />
          </>
        )}
      </div>
      {!loading && d && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="p-5 lg:col-span-7" data-testid="sa-top-vendors">
            <h2 className="font-heading text-lg font-semibold mb-3">Top vendors (by leads)</h2>
            <div style={{ width: "100%", height: 280 }}>
              {d.top_vendors.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-12">No data yet.</p>
              ) : (
                <ResponsiveContainer>
                  <BarChart data={d.top_vendors} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                    <XAxis dataKey="business" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                    <Bar dataKey="leads" radius={[8,8,0,0]} fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
          <Card className="p-5 lg:col-span-5" data-testid="sa-channel-breakdown">
            <h2 className="font-heading text-lg font-semibold mb-3">Messages by channel</h2>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {pieData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
      {!loading && d && (
        <Card className="p-5" data-testid="sa-vendor-status">
          <h2 className="font-heading text-lg font-semibold mb-3">Vendor status</h2>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-amber-400 border-amber-400/40">{d.vendors.pending} pending</Badge>
            <Badge variant="outline" className="text-emerald-400 border-emerald-400/40">{d.vendors.active} active</Badge>
            <Badge variant="outline" className="text-red-400 border-red-400/40">{d.vendors.suspended} suspended</Badge>
          </div>
        </Card>
      )}

      {/* City-integrity widget — surfaces orphan totals so they can't
          silently drift back up. Clicks through to the full remediation
          view. Renders even when the base analytics are still loading. */}
      <CityIntegrityWidget loading={integrityLoading} data={integrity} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// City-integrity widget (Phase 20)
// ─────────────────────────────────────────────────────────────────
function CityIntegrityWidget({ loading, data }) {
  if (loading) {
    return (
      <Card className="p-5" data-testid="sa-city-integrity-widget-loading">
        <Skeleton className="h-6 w-56 mb-3" />
        <Skeleton className="h-20 w-full" />
      </Card>
    );
  }
  if (!data || data.error) {
    return null; // silent degrade — never block the rest of the dashboard.
  }

  const needsCity = data.needs_city?.total ?? 0;
  const needsReview = data.needs_review?.total ?? 0;
  const total = needsCity + needsReview;

  // All-clean state — celebrate briefly so operators know the
  // migration is at 100% coverage.
  if (total === 0) {
    return (
      <Card className="p-5 border-emerald-400/40" data-testid="sa-city-integrity-widget">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-500/15 text-emerald-500 grid place-items-center">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-semibold">City integrity</h2>
            <p className="text-sm text-muted-foreground">
              All vendors and campaigns have a canonical <span className="font-mono">city_id</span>. Franchise scoping is at 100% coverage.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const cov = data.coverage; // may be undefined depending on endpoint version
  const catalog = data.cities?.length ?? 0;

  return (
    <Card
      className={`p-5 border-2 ${needsReview > 0 ? "border-amber-400/60" : "border-primary/40"}`}
      data-testid="sa-city-integrity-widget"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl grid place-items-center ${
            needsReview > 0
              ? "bg-amber-500/15 text-amber-500"
              : "bg-primary/15 text-primary"
          }`}>
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-semibold">City integrity</h2>
            <p className="text-sm text-muted-foreground">
              Records where the Phase-20 <span className="font-mono">city_id</span> backfill needs help. Every one is a hole in franchise scoping.
            </p>
          </div>
        </div>
        <Link
          to="/superadmin/city-integrity"
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
          data-testid="sa-city-integrity-widget-link"
        >
          Open remediation view <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link
          to="/superadmin/city-integrity?tab=needs-city"
          className="rounded-lg border p-3 hover:border-primary hover:bg-primary/5 transition-colors block"
          data-testid="sa-city-integrity-needs-city"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <MapPin className="h-4 w-4" /> Needs city
          </div>
          <div className="text-3xl font-black tabular-nums mt-1">{needsCity}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {data.needs_city?.vendors?.length ?? 0} vendors · {data.needs_city?.campaigns?.length ?? 0} campaigns
          </div>
        </Link>

        <Link
          to="/superadmin/city-integrity?tab=needs-review"
          className={`rounded-lg border p-3 hover:border-amber-400 hover:bg-amber-500/10 transition-colors block ${
            needsReview > 0 ? "border-amber-400/60 bg-amber-500/5" : ""
          }`}
          data-testid="sa-city-integrity-needs-review"
        >
          <div className={`flex items-center gap-2 text-xs uppercase tracking-wider ${
            needsReview > 0 ? "text-amber-500" : "text-muted-foreground"
          }`}>
            <ShieldAlert className="h-4 w-4" /> Needs review
          </div>
          <div className="text-3xl font-black tabular-nums mt-1">{needsReview}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {data.needs_review?.vendors?.length ?? 0} vendors · {data.needs_review?.campaigns?.length ?? 0} campaigns
          </div>
        </Link>

        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <CheckCircle2 className="h-4 w-4" /> Catalog
          </div>
          <div className="text-3xl font-black tabular-nums mt-1">{catalog}</div>
          <div className="text-xs text-muted-foreground mt-1">Active cities available</div>
        </div>
      </div>

      {/* Coverage bars come from the /franchise/self-check endpoint's
          coverage sub-object when the widget is embedded there; on the
          plain integrity endpoint we don't have them, so this block is
          only rendered if we happen to receive them. */}
      {cov && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
          {[
            { label: "Vendors", ready: cov.vendors_with_city_id, total: cov.vendors_total },
            { label: "Campaigns", ready: cov.campaigns_with_city_id, total: cov.campaigns_total },
            { label: "Franchise operators", ready: cov.franchise_users_with_city_id, total: cov.franchise_users_total },
          ].map((row) => {
            const pct = row.total ? Math.round(((row.ready || 0) / row.total) * 100) : 0;
            return (
              <div key={row.label} className="rounded border p-2">
                <div className="text-muted-foreground">{row.label}</div>
                <div className="tabular-nums"><b>{row.ready ?? 0}</b> / {row.total ?? 0} ready ({pct}%)</div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
